import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Spin,
  Alert,
} from 'antd';
import { ArrowLeftOutlined, TrophyOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot, getDocs, query, where, collection } from 'firebase/firestore';
import type { ColumnsType } from 'antd/es/table';
import { db, COLLECTIONS } from '../config/firebase';
import apiClient from '../services/api';
import { COLORS } from '../theme';

const { Title, Text } = Typography;

type LeaderboardRow = {
  rank: number;
  student_id: string;
  student_name: string;
  student_email: string;
  correct_answers: number;
  total_questions: number;
  score: number;
  total_time_seconds: number | null;
  completed_at?: string;
};

function sessionEndedMs(data: Record<string, unknown>): number {
  const ended = data.ended_at as { toMillis?: () => number; seconds?: number } | undefined;
  if (ended?.toMillis) return ended.toMillis();
  if (typeof ended?.seconds === 'number') return ended.seconds * 1000;
  return 0;
}

export default function LiveTestResultsPage() {
  const { liveTestId } = useParams<{ liveTestId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [liveTest, setLiveTest] = useState<{
    id: string;
    title?: string;
    subject?: string;
    status?: string;
    total_questions?: number;
  } | null>(null);
  const [firestoreSessionId, setFirestoreSessionId] = useState<string | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const loadSummary = useCallback(async (sessionId: string) => {
    setSummaryError(null);
    try {
      const res = await apiClient.get<{ leaderboard: LeaderboardRow[] }>(
        `/live-tests/sessions/${sessionId}/summary`
      );
      setLeaderboard(res.data.leaderboard || []);
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { error?: string }; status?: number } })?.response?.status === 404
          ? 'Results are not in the database yet (session may not have been saved when the test ended).'
          : 'Could not load results from the server.';
      setSummaryError(msg);
      setLeaderboard([]);
    }
  }, []);

  useEffect(() => {
    if (!liveTestId) return;

    const unsub = onSnapshot(doc(db, COLLECTIONS.LIVE_TESTS, liveTestId), (snap) => {
      if (snap.exists()) {
        setLiveTest({ id: snap.id, ...snap.data() } as typeof liveTest);
      } else {
        setLiveTest(null);
        message.warning('Live test not found.');
      }
    });

    return () => unsub();
  }, [liveTestId]);

  useEffect(() => {
    if (!liveTestId) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, COLLECTIONS.LIVE_TEST_SESSIONS),
            where('live_test_id', '==', liveTestId)
          )
        );

        const completed = snap.docs
          .map((d) => ({ id: d.id, ...d.data() } as Record<string, unknown> & { id: string }))
          .filter((s) => s.status === 'completed')
          .sort((a, b) => sessionEndedMs(b) - sessionEndedMs(a));

        if (cancelled) return;

        if (completed.length === 0) {
          setFirestoreSessionId(null);
          setLeaderboard([]);
          setSummaryError(
            'No completed session found for this test in Firestore. Finish the test from Control, or results were never saved.'
          );
          return;
        }

        const latestSessionId = completed[0].id;
        setFirestoreSessionId(latestSessionId);
        await loadSummary(latestSessionId);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          message.error('Failed to load session data.');
          setSummaryError('Failed to load sessions from Firestore.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [liveTestId, loadSummary]);

  const formatDuration = (sec: number | null | undefined) => {
    if (sec == null || !Number.isFinite(sec)) return '—';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}m ${s}s`;
  };

  const columns: ColumnsType<LeaderboardRow> = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      width: 72,
      render: (r: number) => (
        <Space>
          {r === 1 && <TrophyOutlined style={{ color: '#faad14' }} />}
          <Text strong>{r}</Text>
        </Space>
      ),
    },
    { title: 'Student', dataIndex: 'student_name', ellipsis: true },
    { title: 'Email', dataIndex: 'student_email', ellipsis: true, render: (v) => v || '—' },
    {
      title: 'Score',
      key: 'score',
      width: 100,
      render: (_, row) => <Text strong>{row.score != null ? `${row.score.toFixed(1)}%` : '—'}</Text>,
    },
    {
      title: 'Correct',
      key: 'correct',
      width: 100,
      render: (_, row) => `${row.correct_answers ?? 0} / ${row.total_questions ?? 0}`,
    },
    {
      title: 'Time',
      dataIndex: 'total_time_seconds',
      width: 100,
      render: (v) => formatDuration(v),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/live-tests')}
        style={{ paddingLeft: 0, marginBottom: 8 }}
      >
        Live tests
      </Button>

      {liveTest && (
        <div style={{ marginBottom: 16 }}>
          <Title level={3} style={{ marginBottom: 4, color: COLORS.text }}>
            {liveTest.title || 'Live test results'}
          </Title>
          <Space wrap>
            {liveTest.subject && <Tag>{liveTest.subject}</Tag>}
            {liveTest.status && <Tag color="default">{liveTest.status}</Tag>}
            {typeof liveTest.total_questions === 'number' && (
              <Text type="secondary">{liveTest.total_questions} questions</Text>
            )}
            {firestoreSessionId && (
              <Text type="secondary" code style={{ fontSize: 12 }}>
                Session: {firestoreSessionId.slice(0, 12)}…
              </Text>
            )}
          </Space>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large">
            <div style={{ minHeight: 48 }} />
          </Spin>
        </div>
      ) : (
        <Card style={{ borderRadius: 12, border: `1px solid ${COLORS.border}` }}>
          {summaryError && (
            <Alert type="warning" message={summaryError} showIcon style={{ marginBottom: 16 }} />
          )}
          <Table<LeaderboardRow>
            rowKey={(r) => `${r.student_id}-${r.rank}`}
            columns={columns}
            dataSource={leaderboard}
            pagination={leaderboard.length > 12 ? { pageSize: 12 } : false}
            locale={{ emptyText: summaryError ? 'No rows' : 'No student results for this session.' }}
          />
        </Card>
      )}
    </div>
  );
}
